// vue
import { h, ref, computed, onMounted } from 'vue';

// store
import { useGl } from '@store/ts/gl';
import { useBots } from '@store/bots';
import { useRefs } from '@store/ts/refs';
import { useStats } from '@store/stats';
import { useWallets } from '@store/wallets';
import { useApiKeys } from '@store/apiKeys';
import { useProfile } from '@store/profile';
import { useMarketplace } from '@store/ts/marketplace';

// router
import { useRoute, useRouter } from 'vue-router';

// i18n
import { useI18n } from 'vue-i18n';

// naive-ui
import {
    NIcon,
    NTag,
    useNotification } from 'naive-ui';

// services
import BotsService from'@services/bots';

// icons
import {
    Edit20Filled,
    Info24Filled,
    Edit20Regular,
    Cart20Regular,
    Play16Regular,
    Stop20Regular,
    CopyAdd24Regular,
    RecordStop28Regular,
    ArrowSync12Regular,
    PauseCircle24Regular,
    ChartMultiple20Regular,
    DeleteDismiss24Regular,
    ArchiveMultiple16Filled,
    ArrowTrendingLines20Filled } from '@vicons/fluent';

export default function (props, { emit }) {
    // store
    const gl = useGl();
    const bots = useBots();
    const refs = useRefs();
    const stats = useStats();
    const wallets = useWallets();
    const apiKeys = useApiKeys();
    const profile = useProfile();
    const marketplace = useMarketplace();

    // router
    const router = useRouter();

    // i18n
    const { t } = useI18n();

    // naive-ui
    const notification = useNotification();

    const showConfirmation = ref(false);
    const showConfirmationCheck = ref(false);
    const confirmLabel = ref();
    const declineMessage = ref();
    const genConfCheck = ref(false);
    const action = ref();

    const functions = {
        marketplace_Buy__review: () => router.push({ name: 'marketplace.buy', params: { bot_id: props.row?.id } }),
        marketplace_free_buy__review: () => router.push({ name: 'marketplace.buy.free', params: { free_bot_id: props.row?.id } }),
        marketplace_Sell__addToMarket: () => props.row.in_market
            ? emit('doSomething', ['tryDeleteBot', props.row.id])
            : router.push({ name: 'marketplace.sell', params: { id: props.row.id } }),
        marketplace_Sell__statistics: () => router.push({ name: 'trade-statistic.management', params: { id: props.row?.id } }),
        marketplace_Sell__edit: () => emit('doSomething', ['tryEditBot', props.row.id]),
        marketplace_mentors__need_help: () => emit('doSomething', ['needHelp', props.row.name]),
        marketplace_mentors__question_info: () => emit('doSomething', ['onQuestionInfo', props.row.id]),
        bots__start: () => canDoAction('start'),
        bots__restart: () => canDoAction('restart'),
        bots__stop: () => canDoAction('stop'),
        bots__pause: () => canDoAction('pause'),
        bots__edit: () => router.push({ name: 'bots.edit', params: { id: props.row.id } }),
        bots__delete: () => canDoAction('delete'),
        bots__stats: () => router.push({ name: 'trade-statistic.management', params: { id: props.row.id } }),
        bots__clone: () => router.push({ name: 'bots.clone', params: { id: props.row.id } }),
        bots__clone_more: () => router.push({ name: 'bots.clone', params: { id: props.row.id } }),
        bots__archive: () => props.row.status == -5 ? canDoAction('unarchive') : canDoAction('archive'),
        bots__force_stop: () => canDoAction('force_stop'),
        wallets__edit: () => emit('doSomething', ['walletsEdit', props.row]),
        wallets__delete: () => emit('doSomething', ['walletsDelete', props.row.id]),
        api_keys__play: () => emit('doSomething', ['play', props.row.id]),
        api_keys__edit: () => emit('doSomething', ['edit', props.row.id]),
        api_keys__delete: () => emit('doSomething', ['delete', props.row.id]),
        q_auth__delete: () => emit('doSomething', ['delete', props.row.id]),
        marketplace_avail_signals: () => emit('doSomething', ['showInfo', props.row]),
        marketplace_my_signals: () => emit('doSomething', ['showInfo', props.row]),
    };

    const colors = computed(() => ({
        'q_auth__delete': 'red-500',
        'marketplace_Sell__addToMarket':
          props.row.in_market ? 'red-500' : undefined,
    }));

    const renderIcon = (icon) => {
        return () => {
            return h(NIcon, null, {
                default: () => h(icon),
            });
        };
    };

    const actionsPrepareMoreOptions = computed(() => {
        if (actionsPrepare.value.length > (gl.isMobile ? 2 : 3)) {
            return actionsPrepare.value.slice(3).map(el => ({
                label: el.popover,
                key: el.id,
                ...el,
                icon: renderIcon(el.icon(props.row)),
            }));
        } else return [];
    });

    const canDoAction = async curAction => {
        gl.showLoading = true;

        action.value = curAction;
        confirmLabel.value = null;
        declineMessage.value = null;

        if (curAction === 'start') {
            try {
                const result = await BotsService.canDoAction({
                    id: props.row.id,
                    action: curAction,
                });

                if (result) {
                    if (result.data.confirm_label && result.data.decline_message) {
                        showConfirmationCheck.value = true;

                        confirmLabel.value = result.data.confirm_label;
                        declineMessage.value = result.data.decline_message;
                    } else {
                        showConfirmation.value = true;
                    }
                }
            } catch {
                notification.error({
                    content: t('errorMessage'),
                    duration: 2500,
                    keepAliveOnHover: true,
                });
            };
        }  else {
            showConfirmation.value = true;
        }

        gl.showLoading = false;
    };

    const onGeneralConfirmClicked = async curAction => {
        gl.showLoading = true;

        try {
            let result;

            if (curAction === 'delete') {
                result = await BotsService.delete({
                    ids: [props.row.id],
                });
            } else {
                result = await BotsService.action({
                    id: props.row.id,
                    action: curAction,
                });
            }

            if (result) {
                // show messages
                if (result.postMessages)
                    result.postMessages.forEach(el => {
                        gl.showNotification({
                            type: el.success ? 'success' : 'error',
                            msg: el.msg,
                        });
                    });

                if (result.data.status) {
                    return void emit('getData');
                }
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const colorsTextType = {
        'error': 'text-red-500 hover:bg-red-200 dark:hover:bg-red-200 hover:bg-opacity-20 dark:hover:bg-opacity-20',
        'default': 'text-gray-600 dark:text-white/75 hover:bg-gray-100 dark:hover:bg-inputBackground',
    };

    const customRender = el => {
        return h(
            'div', {
                class: `w-full h-full flex items-center py-2 px-2 cursor-pointer ${colorsTextType[el.textType]}`,
                onClick: () => {
                    el.fn(props.row);
                },
            }, [
                h(NIcon, {
                    size: 18,
                    class: 'mr-2',
                }, {
                    default: () => h(el.icon(props.row)),
                }),
                el.popover(props.row),
                el.tag
                    ? h(
                        NTag, {
                            round: true,
                            size: 'small',
                            bordered: false,
                            type: el.tag.type,
                            class: 'ml-4',
                        },
                        h('span', { class: 'text-yellow-500 font-semibold' }, el.tag.text),
                    )
                    : null,
            ],
        );
    };

    const actionsPrepare = computed(() => {
        const res = props.data.map(el => ({
            textType: 'default',
            ...el,
        }))
            .filter(el => !el?.custom);

        return res.map(el => ({
            ...el,
            type: 'render',
            render: () => customRender(el),
            popover: el.popover(props.row),
        }));
    });

    // может быть только 1 кастомный экшн
    const customAction = computed(() => {
        return props.data.find(el => el?.custom && el?.show(props.row));
    });

    const handleSelect = $event => {
        functions[$event]();
    };

    return {
        gl,
        refs,
        action,
        colors,
        functions,
        customAction,
        genConfCheck,
        confirmLabel,
        actionsPrepare,
        declineMessage,
        showConfirmation,
        showConfirmationCheck,
        actionsPrepareMoreOptions,
        handleSelect,
        onGeneralConfirmClicked,
    };
}